import { isa } from "@p0security/util/types/is";

import type { RegisteredIntegrationKey } from "./registry-keys.ts";
import { RegisteredIntegrationKeys } from "./registry-keys.ts";

const MISSING_REGISTRATION_HELP =
  'import "@p0security/integrations-registry" first, before anything that reads the registry';

/** Wraps the registry so that reading it before every integration has
 * registered throws, rather than silently reporting a start-cased label and an
 * empty access set.
 *
 * `ownKeys` is the trap that matters: a module-scope `Object.keys`, `mapValues`
 * or spread never asks `get` for the key that isn't there. Off in production,
 * where the ordering is either right or the dev build already failed.
 */
export const sealGuard = <T extends object>(
  target: Record<RegisteredIntegrationKey, T>
): Record<RegisteredIntegrationKey, T> => {
  if (
    typeof process !== "undefined" &&
    process.env?.NODE_ENV === "production"
  ) {
    return target;
  }

  const missingKeys = () =>
    RegisteredIntegrationKeys.filter((key) => !(key in target));

  const fail = (operation: string, missing: readonly string[]): never => {
    const detail =
      missing.length === RegisteredIntegrationKeys.length
        ? "no integrations have registered"
        : `${missing.length} integration(s) have not registered: ${missing.join(", ")}`;
    throw new Error(
      `The integration registry was read (${operation}) while ${detail}. Composition roots must ${MISSING_REGISTRATION_HELP}.`
    );
  };

  return new Proxy(target, {
    get(proxied, property, receiver) {
      // Only a *totally* empty registry is a bug. A partially-populated one is
      // a test-worker artifact -- an entry point imports the whole barrel or
      // none of it -- and `ownKeys` still holds the whole set to account.
      if (
        typeof property === "string" &&
        isa(RegisteredIntegrationKeys, property) &&
        Object.keys(proxied).length === 0
      ) {
        fail(`read of '${property}'`, RegisteredIntegrationKeys);
      }
      return Reflect.get(proxied, property, receiver);
    },
    ownKeys(proxied) {
      const missing = missingKeys();
      if (missing.length > 0) fail("enumeration", missing);
      return Reflect.ownKeys(proxied);
    },
  });
};
